import { useAuth } from '../contexts/AuthContext'
import { UserRole, PermissionResource, PermissionAction } from '../types'
import type { UserPermissions } from '../types'

/**
 * Hook for component-level permission checks
 */
export function usePermissions() {
  const { user, userPermissions, hasPermission, hasRole } = useAuth()

  return {
    // User and permissions data
    user,
    userPermissions,

    // Permission checking methods
    hasPermission,
    hasRole,

    // Convenience methods for common permission checks
    canManageUsers: () => userPermissions?.canManageUsers() ?? false,
    canManageRoles: () => userPermissions?.canManageRoles() ?? false,
    canCreateSolicitudes: () =>
      userPermissions?.canCreateSolicitudes() ?? false,
    canEvaluatePostulaciones: () =>
      userPermissions?.canEvaluatePostulaciones() ?? false,
    canViewReports: () => userPermissions?.canViewReports() ?? false,
    canExportReports: () => userPermissions?.canExportReports() ?? false,
    canQualifyCandidates: () =>
      userPermissions?.canQualifyCandidates() ?? false,
    canBulkQualifyCandidates: () =>
      userPermissions?.canBulkQualifyCandidates() ?? false,

    // Role checking convenience methods
    isAdmin: () => hasRole(UserRole.ADMINISTRADOR),
    isEditor: () => hasRole(UserRole.EDITOR),
    isViewer: () => hasRole(UserRole.VISUALIZADOR),

    // Resource-specific permission checks
    canCreateUsers: () =>
      hasPermission(PermissionResource.USERS, PermissionAction.CREATE),
    canReadUsers: () =>
      hasPermission(PermissionResource.USERS, PermissionAction.READ),
    canUpdateUsers: () =>
      hasPermission(PermissionResource.USERS, PermissionAction.UPDATE),
    canDeleteUsers: () =>
      hasPermission(PermissionResource.USERS, PermissionAction.DELETE),

    canCreateRoles: () =>
      hasPermission(PermissionResource.ROLES, PermissionAction.CREATE),
    canReadRoles: () =>
      hasPermission(PermissionResource.ROLES, PermissionAction.READ),
    canUpdateRoles: () =>
      hasPermission(PermissionResource.ROLES, PermissionAction.UPDATE),
    canDeleteRoles: () =>
      hasPermission(PermissionResource.ROLES, PermissionAction.DELETE),

    canCreateSolicitud: () =>
      hasPermission(PermissionResource.SOLICITUDES, PermissionAction.CREATE),
    canReadSolicitudes: () =>
      hasPermission(PermissionResource.SOLICITUDES, PermissionAction.READ),
    canUpdateSolicitudes: () =>
      hasPermission(PermissionResource.SOLICITUDES, PermissionAction.UPDATE),
    canDeleteSolicitudes: () =>
      hasPermission(PermissionResource.SOLICITUDES, PermissionAction.DELETE),

    canReadPostulaciones: () =>
      hasPermission(PermissionResource.POSTULACIONES, PermissionAction.READ),
    canEvaluatePostulacion: () =>
      hasPermission(
        PermissionResource.POSTULACIONES,
        PermissionAction.EVALUATE
      ),
    canUpdatePostulacionStatus: () =>
      hasPermission(
        PermissionResource.POSTULACIONES,
        PermissionAction.UPDATE_STATUS
      ),

    canViewReportsData: () =>
      hasPermission(PermissionResource.REPORTS, PermissionAction.VIEW),
    canExportReportsData: () =>
      hasPermission(PermissionResource.REPORTS, PermissionAction.EXPORT),

    canQualifyCandidate: () =>
      hasPermission(PermissionResource.POSTULACIONES, PermissionAction.QUALIFY),
    canBulkQualifyCandidate: () =>
      hasPermission(
        PermissionResource.POSTULACIONES,
        PermissionAction.BULK_QUALIFY
      ),
  }
}

/**
 * Hook for checking if user has any of the specified roles
 */
export function useHasAnyRole(roles: UserRole[]) {
  const { hasRole } = usePermissions()

  return roles.some(role => hasRole(role))
}

/**
 * Hook for checking if user has all of the specified roles
 */
export function useHasAllRoles(roles: UserRole[]) {
  const { hasRole } = usePermissions()

  return roles.every(role => hasRole(role))
}

/**
 * Hook for checking multiple permissions at once
 */
export function useHasPermissions(
  permissions: Array<{
    resource: PermissionResource
    action: PermissionAction
  }>
) {
  const { hasPermission } = usePermissions()

  return {
    hasAll: permissions.every(({ resource, action }) =>
      hasPermission(resource, action)
    ),
    hasAny: permissions.some(({ resource, action }) =>
      hasPermission(resource, action)
    ),
    results: permissions.map(({ resource, action }) => ({
      resource,
      action,
      allowed: hasPermission(resource, action),
    })),
  }
}

/**
 * Hook for route-level permission checking
 */
export function useRoutePermissions() {
  const permissions = usePermissions()

  return {
    ...permissions,

    // Route access checks
    canAccessUserManagement: () => permissions.canManageUsers(),
    canAccessRoleManagement: () => permissions.canManageRoles(),
    canAccessDashboard: () => true, // All authenticated users can access dashboard
    canAccessSolicitudes: () => permissions.canReadSolicitudes(),
    canAccessPostulaciones: () => permissions.canReadPostulaciones(),
    canAccessReports: () => permissions.canViewReports(),

    // Navigation menu visibility checks
    shouldShowUserManagement: () => permissions.canManageUsers(),
    shouldShowRoleManagement: () => permissions.canManageRoles(),
    shouldShowCreateSolicitud: () => permissions.canCreateSolicitudes(),
    shouldShowReports: () => permissions.canViewReports(),
    shouldShowAdminSection: () => permissions.isAdmin(),
  }
}
